import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuditByQuizId } from "@/hooks/audits/audit-by-quiz-id.hook";
import { useDownloadAuditResultMutation } from "@/hooks/audits/download-audit-result-mutation.hook";
import { useCanViewResults } from "@/hooks/can-view-results.hook";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useQuizById } from "@/hooks/quiz-by-id.hook";
import { useDownloadResultsFileMutation } from "@/hooks/reports/download-results-file-mutation.hook";
import { currentEditionIdAtom } from "@/store/edition-id.store";
import { ArrowDown2, DocumentSketch, Glass } from "iconsax-react";
import { useAtomValue } from "jotai";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import ReevaluationDrawer from "./reevaluation-drawer/reevaluation-drawer";
import { useDownloadQuizExcelMutation } from "@/hooks/download-quiz-excel-mutation.hook";

const allowQuizReevaluation =
  import.meta.env.VITE_ALLOW_QUIZ_REEVALUATION === "true";

const HeaderActions = () => {
  const editionId = useAtomValue(currentEditionIdAtom);

  const { data: userProfile } = useCurrentUserProfile();

  const { quizId } = useParams();

  const { data: quiz } = useQuizById({
    companyId: userProfile?.companyId || "",
    applicationQuizId: quizId || "",
  });

  const { data: audit } = useAuditByQuizId({
    companyId: userProfile?.companyId || "",
    quizId: quizId || "",
  });

  const downloadResultsMutation = useDownloadResultsFileMutation();

  const downloadAuditResultMuation = useDownloadAuditResultMutation();

  const downloadQuizExcelMutation = useDownloadQuizExcelMutation();

  const handleDownloadAuditResult = () => {
    const promise = downloadAuditResultMuation.mutateAsync({
      companyId: userProfile?.companyId || "",
      auditId: audit?.id || "",
    });
    toast.promise(promise, {
      loading: "Preparando resultado de auditoría...",
      success: () => {
        return "Reporte listo.";
      },
    });
  };

  const options = [
    {
      title: "Descargar reporte de resultados",
      enabled: true,
      callback: () => {
        const promise = downloadResultsMutation.mutateAsync({
          companyId: userProfile?.companyId || "",
          applicationQuizId: quizId || "",
        });
        toast.promise(promise, {
          loading: "Preparando reporte en PDF...",
          success: () => {
            return "Reporte listo.";
          },
        });
      },
    },
    {
      title: "Descargar reporte en Excel",
      enabled: true,
      callback: () => {
        const promise = downloadQuizExcelMutation.mutateAsync({
          companyId: userProfile?.companyId || "",
          applicationQuizId: quizId || "",
        });
        toast.promise(promise, {
          loading: "Preparando reporte en Excel...",
          success: () => {
            return "Reporte en Excel listo.";
          },
        });
      },
    },
  ];

  const canViewResults = useCanViewResults(editionId);

  if (!canViewResults) {
    return null;
  }

  return (
    <div className="flex items-center gap-2">
      {allowQuizReevaluation &&
        (quiz?.status === "REVIEWED" || quiz?.status === "DELIVERED") &&
        !(quiz.appealReviewCount > 0) && (
          <ReevaluationDrawer>
            <Button
              size={"lg"}
              variant={"outline"}
              className="rounded-2xl bg-transparent px-4"
            >
              <Glass />
              Solicitar re-evaluación
            </Button>
          </ReevaluationDrawer>
        )}

      {audit?.status === "AUDITED" && (
        <Button
          size={"lg"}
          variant={"outline"}
          className="rounded-2xl bg-transparent px-4"
          onClick={() => handleDownloadAuditResult()}
        >
          <DocumentSketch />
          Ver resultado de auditoría
        </Button>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size={"lg"}
            variant={"outline"}
            className="rounded-2xl bg-transparent px-4"
          >
            Descargar informe
            <ArrowDown2 />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent>
          {options.map((option, index) => (
            <DropdownMenuItem key={index} onClick={() => option.callback()}>
              {option.title}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default HeaderActions;
